import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { retryWithBackoff } from "@/utils/retry";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Constants for retry and cooldown configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // in milliseconds
const RATE_LIMIT_COOLDOWN = 60; // in seconds

export function ForgotPasswordDialog({ open, onOpenChange }: ForgotPasswordDialogProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { resetPassword } = useAuth();
  const { toast } = useToast();

  // Countdown effect for rate-limit cooldown
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsLoading(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      toast({
        title: "Error",
        description: "Email address is required.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await retryWithBackoff(
        () => resetPassword(trimmedEmail),
        MAX_RETRIES,
        RETRY_DELAY
      );

      toast({
        title: "Check your email",
        description: "We've sent you a password reset link. Please check your email.",
      });

      setEmail("");
      onOpenChange(false);
      setIsLoading(false);
    } catch (error: unknown) {
      console.error("Password reset error:", error);
      const errorMessage = getErrorMessage(error);

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });

      // If a rate-limit error occurred, start the cooldown countdown.
      if (error instanceof Error && error.message.includes("rate limit exceeded")) {
        setCooldown(RATE_LIMIT_COOLDOWN);
      } else {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-labelledby="reset-password-title">
        <DialogHeader className="mb-4">
          <DialogTitle id="reset-password-title">Reset Password</DialogTitle>
          <DialogDescription>
            Enter your email address, and we'll send you a link to reset your password.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="Password reset form">
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                disabled={isLoading || cooldown > 0}
                required
                autoComplete="email"
                aria-invalid={email.trim() ? "false" : "true"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading || cooldown > 0}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || cooldown > 0}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {cooldown > 0 ? `Wait ${cooldown}s` : "Send Reset Link"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Extracts a user-friendly error message from an unknown error.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes("rate limit exceeded")) {
      return "Too many attempts. Please wait a minute before trying again.";
    }
    if (error.message.includes("timeout") || error.message.includes("504")) {
      return "The server is taking too long to respond. Please try again.";
    }
    return error.message;
  }
  return "Failed to send the reset link. Please try again.";
}